import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';

import ColorUploadIcon from 'components/system/Icons/ColorUploadIcon';
import TrashIcon from 'components/system/Icons/TrashIcon';
import { Pagination } from 'components/system/Pagination';
import { usePagination } from 'components/system/Pagination/hooks/usePagination';
import ErrorMessage from './components/ErrorMessage/ErrorMessage';
import ImagePreview from './components/ImagePreview/ImagePreview';
import VideoPreview from './components/VideoPreview/VideoPreview';
import { useDropzone } from './dropzone.hooks';
import {
  DropzoneContainer,
  DropzoneOuterContainer,
  LabelWrapper,
  PreviewContainer,
  PreviewsContainer,
  PreviewsOuterContainer,
  RemoveButton,
  RemoveButtonContainer,
  StyledDropzone,
} from './dropzone.styles';
import { IDropzone, IPreview } from './dropzone.types';
import { formatDropzoneLabel } from './helpers';

const Dropzone = ({
  setValue,
  files,
  maxFiles,
  acceptedExtensions,
  clearYupErrors,
  yupError,
  isUploading,
}: IDropzone) => {
  const disabled = isUploading || files?.length >= maxFiles;

  const {
    getRootProps,
    getInputProps,
    isFocused,
    previews,
    removeFile,
    errorMessage,
    clearErrors,
  } = useDropzone({
    setValue,
    files,
    maxFiles,
    acceptedExtensions,
    disabled,
    clearYupErrors,
    yupError,
  });

  // # PREVIEWS GALLERY
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const pageSize = isSmallScreen ? 3 : 5;

  const {
    page,
    total,
    totalPages,
    startIndex,
    endIndex,
    paginatedItems,
    handlePrev,
    handleNext,
  } = usePagination<IPreview>({
    items: previews,
    pageSize,
  });

  return (
    <section>
      <DropzoneOuterContainer
        sx={{
          opacity: isUploading ? '0.6' : '1',
          cursor: isUploading ? 'progress' : 'default',
        }}
      >
        <DropzoneContainer>
          <StyledDropzone
            {...getRootProps()}
            aria-label="Upload files"
            isFocused={isFocused}
            disabled={disabled}
            error={Boolean(errorMessage)}
          >
            <input {...getInputProps()} data-testid="dropzone-input" />
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              height="200px"
              position="relative"
            >
              <Box display="flex" flexDirection="column" paddingBottom={6}>
                <LabelWrapper>
                  <>
                    <ColorUploadIcon />
                    {'Click to upload file'}
                    <Typography sx={{ color: 'text.secondary' }}>
                      {formatDropzoneLabel(maxFiles, acceptedExtensions)}
                    </Typography>
                  </>
                </LabelWrapper>
              </Box>
              <Box height="50px" position="absolute" bottom="10px" width="100%">
                <ErrorMessage
                  errorMessage={errorMessage}
                  clearErrors={clearErrors}
                />
              </Box>
            </Box>
          </StyledDropzone>
        </DropzoneContainer>
        {previews.length > 0 ? (
          <aside>
            <PreviewsOuterContainer>
              <PreviewsContainer>
                {paginatedItems.map((preview) => {
                  return (
                    <PreviewContainer key={preview.name}>
                      {preview.type === 'video' ? (
                        <VideoPreview preview={preview} />
                      ) : (
                        <ImagePreview preview={preview} />
                      )}

                      {!isUploading && (
                        <RemoveButtonContainer>
                          <RemoveButton
                            onClick={() => removeFile(preview.name)}
                            aria-label={`remove file ${preview.name}`}
                          >
                            <TrashIcon />
                          </RemoveButton>
                        </RemoveButtonContainer>
                      )}
                    </PreviewContainer>
                  );
                })}
              </PreviewsContainer>

              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                startIndex={startIndex}
                endIndex={endIndex}
                handlePrev={handlePrev}
                handleNext={handleNext}
              />
            </PreviewsOuterContainer>
          </aside>
        ) : null}
      </DropzoneOuterContainer>
    </section>
  );
};

export default Dropzone;
